import { useMemo, useCallback } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useLanguage } from '../../locale/useLanguage';
import {
  // PremiseType,
  CmsConfigs,
  WeekDay,
  AddressType,
} from '../../models';
import {
  getTotalHours,
  validateConciergeHours,
} from '../../views/OpeningHours/openingHoursHelper';
import { useOrder } from '../useOrder';
import dayjs from 'dayjs';

export type AddressNoteForm = {
  intercomName: string;
  floor: number;
  hasElevator: boolean;
  notes: string;
  addressName: string;
  isSavingAddress: boolean;
  isReception: boolean;
  addressType?: AddressType;
  // premiseType?: PremiseType;
  days?: WeekDay[];
  receptionMorningFromHour: string;
  receptionMorningToHour: string;
  receptionAfternoonFromHour: string;
  receptionAfternoonToHour: string;
};

export const useAddressNoteValidationForm = (
  notes?: AddressNoteForm,
  cmsConfigs?: Pick<
    CmsConfigs,
    'receptionMinimumHoursPerDay' | 'receptionMinimumDaysPerWeek'
  >,
  isFromChangeAddress?: boolean
) => {
  const [t] = useLanguage();
  const order = useOrder();

  const {
    register,
    handleSubmit,
    setValue: setFormValue,
    control,
    getValues: getFormValues,
    formState: { errors },
    trigger,
  } = useForm<AddressNoteForm>({
    defaultValues: {
      addressType: notes?.addressType,
      isSavingAddress: !isFromChangeAddress || notes?.isSavingAddress,
      addressName: notes?.addressName,
      // premiseType: notes?.premiseType,
      days: notes?.days,
      floor: notes?.floor,
      hasElevator: notes?.hasElevator,
      intercomName: notes?.intercomName,
      isReception: notes?.isReception,
      notes: notes?.notes,
      receptionAfternoonFromHour: notes?.receptionAfternoonFromHour,
      receptionAfternoonToHour: notes?.receptionAfternoonToHour,
      receptionMorningFromHour: notes?.receptionMorningFromHour,
      receptionMorningToHour: notes?.receptionMorningToHour,
    },
  });

  const isSavingAddressController = useController<
    AddressNoteForm,
    'isSavingAddress'
  >({
    control,
    name: 'isSavingAddress',
  });

  const validateReceptionHours = useCallback(() => {
    trigger('days');
    let minimumHours = cmsConfigs?.receptionMinimumHoursPerDay;
    if (isFromChangeAddress && order?.receptionRules?.minRangeWidthPerDay) {
      minimumHours = dayjs
        .duration(order?.receptionRules?.minRangeWidthPerDay)
        .asHours();
    }
    const {
      receptionAfternoonFromHour,
      receptionMorningFromHour,
      receptionAfternoonToHour,
      receptionMorningToHour,
      isReception,
    } = getFormValues();

    if (!isReception) return true;

    return validateConciergeHours({
      minimumHours,
      receptionAfternoonFromHour,
      receptionAfternoonToHour,
      receptionMorningFromHour,
      receptionMorningToHour,
      t,
    });
  }, [
    cmsConfigs?.receptionMinimumHoursPerDay,
    getFormValues,
    t,
    order,
    trigger,
    isFromChangeAddress,
  ]);

  const intercomNameValidation = useMemo(
    () => register('intercomName'),
    [register]
  );

  const floorValidation = useMemo(() => register('floor'), [register]);

  const hasElevatorValidation = useMemo(
    () => register('hasElevator'),
    [register]
  );

  const notesValidation = useMemo(() => register('notes'), [register]);

  const isSavingAddressValidation = useMemo(
    () => register('isSavingAddress'),
    [register]
  );

  const addressNameValidation = useMemo(
    () =>
      register('addressName', {
        required: {
          message: t('addressNotesView.addressNameRequired'),
          value: isSavingAddressController.field.value,
        },
      }),
    [isSavingAddressController.field.value, register, t]
  );

  const { field: receptionField } = useController<
    AddressNoteForm,
    'isReception'
  >({
    control,
    name: 'isReception',
    defaultValue: false,
  });

  const onIsReceptionChange = useCallback(
    (val: boolean) => {
      setFormValue('isReception', val, { shouldValidate: true });
    },
    [setFormValue]
  );

  // const { field: premiseTypeField } = useController<
  //   AddressNoteForm,
  //   'premiseType'
  // >({
  //   control,
  //   name: 'premiseType',
  //   defaultValue: PremiseType.Reception,
  // });

  // const onPremiseTypeChange = useCallback(
  //   (val: PremiseType) => {
  //     setFormValue('premiseType', val, { shouldValidate: true });
  //   },
  //   [setFormValue]
  // );

  const { field: addressTypeField } = useController<
    AddressNoteForm,
    'addressType'
  >({
    control,
    name: 'addressType',
    defaultValue: AddressType.Private,
  });

  const onAddressTypeChange = useCallback(
    (val: AddressType) => {
      setFormValue('addressType', val, { shouldValidate: true });
    },
    [setFormValue]
  );

  const { field: weekDaysField } = useController<AddressNoteForm, 'days'>({
    control,
    name: 'days',
    defaultValue: [],
    rules: {
      validate: (weekDays) => {
        const isReception = getFormValues('isReception');
        if (!isReception) return true;

        const minimumDays = cmsConfigs?.receptionMinimumDaysPerWeek;
        const minimumHoursInWeek =
          isFromChangeAddress && order?.receptionRules?.minRangesWidthPerWeek
            ? dayjs
                .duration(order?.receptionRules?.minRangesWidthPerWeek)
                .asHours()
            : null;

        if (minimumHoursInWeek) {
          const {
            receptionAfternoonFromHour,
            receptionMorningFromHour,
            receptionAfternoonToHour,
            receptionMorningToHour,
          } = getFormValues();
          const dailyHours = getTotalHours(
            receptionMorningFromHour,
            receptionMorningToHour,
            receptionAfternoonFromHour,
            receptionAfternoonToHour
          );
          if (!weekDays)
            return t('addressNotesView.receptionWeekHoursError', {
              hours: minimumHoursInWeek,
            });
          if (dailyHours * weekDays!.length < minimumHoursInWeek) {
            return t('addressNotesView.receptionWeekHoursError', {
              hours: minimumHoursInWeek,
            });
          }
        } else {
          if (isReception && !weekDays)
            return t('addressNotesView.receptionDaysError', {
              days: minimumDays,
            });

          if (minimumDays && weekDays!.length < minimumDays)
            return t('addressNotesView.receptionDaysError', {
              days: minimumDays,
            });
        }

        return true;
      },
    },
  });

  const onWeekDaysChange = useCallback(
    (val: WeekDay) => {
      const indexVal = weekDaysField.value!.findIndex((wd) => wd === val);
      const weekDayAlreadyExist = indexVal >= 0;
      let updatedWeekDays: WeekDay[];
      if (weekDayAlreadyExist) {
        updatedWeekDays = [...weekDaysField.value!];
        updatedWeekDays.splice(indexVal, 1);
      } else {
        updatedWeekDays = [...weekDaysField.value!, val];
      }
      setFormValue('days', updatedWeekDays, { shouldValidate: true });
    },
    [setFormValue, weekDaysField.value]
  );

  const { field: receptionMorningFromHourField } = useController<
    AddressNoteForm,
    'receptionMorningFromHour'
  >({
    control,
    name: 'receptionMorningFromHour',
    defaultValue: '08',
    rules: {
      validate: () => validateReceptionHours(),
    },
  });

  const onReceptionMorningFromHourChange = useCallback(
    (val: string | undefined) => {
      setFormValue('receptionMorningFromHour', val!, {
        shouldValidate: true,
      });
    },
    [setFormValue]
  );

  const { field: receptionMorningToHourField } = useController<
    AddressNoteForm,
    'receptionMorningToHour'
  >({
    control,
    name: 'receptionMorningToHour',
    defaultValue: '13',
    rules: {
      validate: () => validateReceptionHours(),
    },
  });

  const onReceptionMorningToHourChange = useCallback(
    (val: string | undefined) => {
      setFormValue('receptionMorningToHour', val!, {
        shouldValidate: true,
      });
    },
    [setFormValue]
  );

  const { field: receptionAfternoonFromHourField } = useController<
    AddressNoteForm,
    'receptionAfternoonFromHour'
  >({
    control,
    name: 'receptionAfternoonFromHour',
    defaultValue: '14',
    rules: {
      validate: () => validateReceptionHours(),
    },
  });

  const onReceptionAfternoonFromHourChange = useCallback(
    (val: string | undefined) => {
      setFormValue('receptionAfternoonFromHour', val!, {
        shouldValidate: true,
      });
    },
    [setFormValue]
  );

  const { field: receptionAfternoonToHourField } = useController<
    AddressNoteForm,
    'receptionAfternoonToHour'
  >({
    control,
    name: 'receptionAfternoonToHour',
    defaultValue: '18',
    rules: {
      validate: () => validateReceptionHours(),
    },
  });

  const onReceptionAfternoonToHourChange = useCallback(
    (val: string | undefined) => {
      setFormValue('receptionAfternoonToHour', val!, {
        shouldValidate: true,
      });
    },
    [setFormValue]
  );

  return {
    hasElevatorValidation,
    notesValidation,
    onIsReceptionChange,
    receptionField,
    onAddressTypeChange,
    addressTypeField,
    // onPremiseTypeChange,
    // premiseTypeField,
    onReceptionAfternoonToHourChange,
    receptionAfternoonToHourField,
    onReceptionAfternoonFromHourChange,
    receptionAfternoonFromHourField,
    onReceptionMorningToHourChange,
    receptionMorningToHourField,
    onReceptionMorningFromHourChange,
    receptionMorningFromHourField,
    onWeekDaysChange,
    floorValidation,
    addressNameValidation,
    isSavingAddressValidation,
    intercomNameValidation,
    isSavingAddressController,
    errors,
    handleSubmit,
    weekDaysField,
  };
};
